"use client";

/**
 * Day Map — the visual surface for GET /v1/day.
 *
 * Two renderers share one geometry:
 *  - `DaySpine`   the vertical map (time flows DOWN — phones are tall, so
 *                 there is ONE layout, not a mobile and a desktop one).
 *  - `DayStrip`   the compact horizontal overview, reused on /today.
 *
 * Everything is hand-rolled CSS + one small inline SVG. No chart library, no
 * force simulation, no randomness: the layout is a pure function of the
 * response, so it is pixel-identical across renders and refreshes.
 *
 * THE LAYOUT PROBLEM, AND THE HONEST FIX.
 * A strictly time-proportional column is unreadable on a phone: a 30-minute
 * meeting in a 15-hour window is ~15px tall — not legible and nowhere near a
 * 44px tap target. So the *time axis* (hour gutter, place ribbon, moment ticks)
 * is strictly proportional and truthful, while the *rows* are packed to a
 * minimum pitch, exactly like a well-drawn annotated timeline:
 *   1. pick a vertical scale from the day's own rhythm (see `pickScale`),
 *   2. place each row's CENTRE at its true y,
 *   3. sweep forward pushing collisions down by ROW_H,
 *   4. sweep backward so nothing runs off the bottom.
 * The container is sized `>= n * ROW_H`, which makes step 4 always feasible.
 * Where a row has been displaced, a leader line in the rail joins it back to
 * its true tick — the map never silently lies about when something happened,
 * and every row also states its own clock time in text.
 *
 * Measured on a real-shaped 11-moment day at 375px: 6 of 11 rows land exactly
 * on their tick, worst displacement 24px (a pair 32 minutes apart), and the
 * quiet evening stretches visibly open up.
 *
 * The SVG rail is decorative and aria-hidden: every fact it draws (which
 * moments share a topic, when a moment really happened) is also in the DOM.
 */
import Link from "next/link";
import type { DayMapResponse, DayMoment, DayPlaceBand, DayTopic } from "@harryclaw/types";

/**
 * Deep links are given by the API; these are the routes that actually exist in
 * this app. Contacts and meetings have no per-id page (they're list + in-page
 * detail), so point at the list rather than a 404.
 *
 * Lives HERE, next to the only components that render an API-supplied href, so
 * a new caller can't forget it — which is exactly how every meeting bubble
 * shipped pointing at a non-existent /meetings/<uuid>.
 */
export function safeHref(href: string): string {
  if (/^\/contacts\/[^/]+$/.test(href)) return "/contacts";
  if (/^\/meetings(\/|$)/.test(href)) return "/calendar";
  return href;
}

/** Row pitch — 56px of content + 8px gap clears the 44px tap-target floor. */
const ROW_H = 66;
const ROW_CONTENT_H = 58;
/** Never draw a map shorter than this, so a two-moment day still reads as a day. */
const MIN_H = 380;
/** Hard bounds on the vertical scale (px per minute) — see `pickScale`. */
const MIN_SCALE = 0.45;
const MAX_SCALE = 2.0;
/**
 * Preferred maximum height. NOT a hard cap: above ~45 moments the row pitch
 * floor (n * ROW_H) necessarily wins, because every row must still clear a
 * 44px tap target. A very long day becomes a long honest scroll rather than an
 * unreadable compressed one — which is the right trade, but don't read this
 * constant as a ceiling.
 */
const MAX_H = 3000;
/** Width of the SVG rail that carries the topic curves + leader lines. */
const RAIL_W = 24;

export const KIND_META: Record<DayMoment["kind"], { icon: string; label: string; colour: string }> = {
  meeting: { icon: "🗓", label: "Meeting", colour: "#a855f7" },
  // `conversation` is the EMAIL/CHAT kind (built from message bursts, carries a
  // channel and a band) and `capture` is the spoken one (built from transcripts).
  // The labels were the wrong way round: a day of real sit-downs read as
  // "captures" while a run of email read as "conversations".
  conversation: { icon: "💬", label: "Message thread", colour: "#4f8cff" },
  ambient: { icon: "◎", label: "You, out loud", colour: "#22c55e" },
  capture: { icon: "🎙", label: "Conversation", colour: "#f59e0b" },
  focus: { icon: "▦", label: "Focus", colour: "#38bdf8" },
};

/** Channel overrides the generic conversation glyph when we know the channel. */
const CHANNEL_ICON: Record<string, string> = { email: "✉", whatsapp: "💬", teams: "▣" };

/** Place categories — same vocabulary as /places, plus a band colour. */
export const PLACE_META: Record<string, { icon: string; colour: string }> = {
  home: { icon: "🏠", colour: "#22c55e" },
  work: { icon: "💼", colour: "#a855f7" },
  gym: { icon: "🏋", colour: "#f59e0b" },
  regular: { icon: "📍", colour: "#4f8cff" },
  transit: { icon: "🚆", colour: "#64748b" },
  other: { icon: "•", colour: "#6b7280" },
};
export const placeMeta = (c: string) => PLACE_META[c] ?? PLACE_META.other!;

/** Deterministic, index-based — the same topic keeps the same colour on reload. */
const TOPIC_COLOURS = ["#4f8cff", "#f472b6", "#34d399", "#fbbf24"];
export const topicColour = (i: number) => TOPIC_COLOURS[i % TOPIC_COLOURS.length]!;

export function hhmm(min: number): string {
  const m = ((Math.round(min) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

export function fmtMins(n: number): string {
  const v = Math.max(0, Math.round(n));
  if (v < 60) return `${v}m`;
  const h = Math.floor(v / 60);
  const m = v % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** "08:50–17:05", with "…" where the visit ran outside this local day. */
export function bandRange(b: DayPlaceBand): string {
  return `${b.clippedStart ? "…" : hhmm(b.startMin)}–${b.clippedEnd ? "…" : hhmm(b.endMin)}`;
}

export function placeName(b: DayPlaceBand): string {
  if (b.label) return b.label;
  if (b.category === "transit") return "On the move";
  return b.category === "other" ? "Somewhere new" : b.category;
}

export function momentIcon(m: DayMoment): string {
  if (m.kind === "conversation" && m.channel) return CHANNEL_ICON[m.channel] ?? KIND_META.conversation.icon;
  return KIND_META[m.kind].icon;
}

/** "11:00 · 65m · ✉ 2 in / 1 out" — the row's own honest, textual timestamp. */
export function momentMeta(m: DayMoment): string {
  const bits: string[] = [hhmm(m.startMin)];
  const span = m.endMin - m.startMin;
  if (span >= 1) bits.push(fmtMins(span));
  const { inbound, outbound, items, durationMins } = m.stats;
  if (typeof items === "number") bits.push(`${items} item${items === 1 ? "" : "s"}`);
  if (typeof inbound === "number" || typeof outbound === "number") {
    bits.push(`${inbound ?? 0} in / ${outbound ?? 0} out`);
  } else if (span < 1 && typeof durationMins === "number" && durationMins > 0) {
    bits.push(fmtMins(durationMins));
  }
  return bits.join(" · ");
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

export interface MomentRow {
  m: DayMoment;
  /** Where the moment actually is on the time axis. */
  tickY: number;
  /** Top of the drawn row after packing. */
  y: number;
  /** Vertical centre of the drawn row — equals `tickY` when undisplaced. */
  cy: number;
}

export interface SpineLayout {
  height: number;
  /** px per minute. */
  scale: number;
  yOf: (min: number) => number;
  rows: MomentRow[];
  /** Hour lines, already filtered to the window. */
  ticks: { min: number; y: number }[];
}

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

function hourStep(scale: number): number {
  for (const step of [60, 120, 180, 240]) if (step * scale >= 34) return step;
  return 360;
}

/**
 * The scale is derived from the day's own rhythm, not a constant — and this is
 * the difference between a map and a list.
 *
 * With a fixed px-per-minute, the packing sweep CASCADES: the first pair of
 * moments that sit closer than ROW_H apart pushes the next one down, which
 * pushes the next, and the debt never clears — so an 11-moment day renders as
 * eleven evenly-spaced rows with no shape at all (measured: exactly 62px pitch
 * top to bottom). Scaling so that the MEDIAN gap between moments is about one
 * row tall means a typical gap clears the debt instead of inheriting it:
 * clusters still pack tight, and real quiet stretches actually open up.
 *
 * Median (not mean) so one long evening gap can't stretch the whole day, and a
 * little headroom on top (a median gap gets 1.25 rows, not exactly 1) because
 * at exactly 1 row half of all gaps collide by construction.
 */
const GAP_HEADROOM = 1.25;

function pickScale(startMins: number[]): number {
  const gaps: number[] = [];
  for (let i = 1; i < startMins.length; i++) {
    const g = startMins[i]! - startMins[i - 1]!;
    if (g > 0) gaps.push(g);
  }
  if (gaps.length === 0) return MIN_SCALE;
  gaps.sort((a, b) => a - b);
  const median = gaps[Math.floor(gaps.length / 2)]!;
  return clamp((ROW_H * GAP_HEADROOM) / clamp(median, 12, 180), MIN_SCALE, MAX_SCALE);
}

/**
 * Pure function of (moments, window) — no state, no randomness, no measurement
 * of the DOM. Same input, same pixels, every render.
 */
export function layoutSpine(moments: DayMoment[], win: { startMin: number; endMin: number }): SpineLayout {
  const span = Math.max(60, win.endMin - win.startMin);
  const sorted = [...moments].sort((a, b) => a.startMin - b.startMin || a.id.localeCompare(b.id));
  // The floor `n * ROW_H` is what makes the backward sweep below always feasible.
  const height = clamp(
    Math.round(span * pickScale(sorted.map((m) => m.startMin))),
    Math.max(MIN_H, sorted.length * ROW_H),
    Math.max(MAX_H, sorted.length * ROW_H),
  );
  const scale = height / span;
  const yOf = (min: number) => clamp((min - win.startMin) * scale, 0, height);

  // Pack row CENTRES, not tops: an undisplaced row then sits exactly on its own
  // tick and its leader line is dead horizontal. Packing tops instead puts every
  // row half a row-height below its true time — a small, systematic lie.
  const half = ROW_CONTENT_H / 2;
  const cys = new Array<number>(sorted.length);
  let prev = -Infinity;
  for (let i = 0; i < sorted.length; i++) {
    const c = Math.max(yOf(sorted[i]!.startMin), half, prev + ROW_H);
    cys[i] = c;
    prev = c;
  }
  for (let i = sorted.length - 1; i >= 0; i--) {
    const cap = i === sorted.length - 1 ? height - half : cys[i + 1]! - ROW_H;
    if (cys[i]! > cap) cys[i] = Math.max(half, cap);
  }

  const step = hourStep(scale);
  const ticks: { min: number; y: number }[] = [];
  for (let min = Math.ceil(win.startMin / step) * step; min <= win.endMin; min += step) {
    ticks.push({ min, y: yOf(min) });
  }

  return {
    height,
    scale,
    yOf,
    ticks,
    rows: sorted.map((m, i) => ({ m, tickY: yOf(m.startMin), y: cys[i]! - half, cy: cys[i]! })),
  };
}

// ---------------------------------------------------------------------------
// The vertical map
// ---------------------------------------------------------------------------

export function DaySpine({
  data,
  activeTopic,
  onPickMoment,
}: {
  data: DayMapResponse;
  /** Dim everything outside this topic. null = show all. */
  activeTopic: string | null;
  onPickMoment?: (m: DayMoment) => void;
}) {
  const win = data.window;
  const layout = layoutSpine(data.moments, win);
  const { height, yOf, rows, ticks } = layout;

  const topicIndex = new Map(data.topics.map((t, i) => [t.id, i]));
  const rowById = new Map(rows.map((r) => [r.m.id, r]));
  const peopleById = new Map(data.people.map((p) => [p.id, p]));

  // Bands clipped to the drawn window (the window is already clamped server-side).
  const bands = data.places
    .map((b) => ({ b, top: yOf(Math.max(b.startMin, win.startMin)), bottom: yOf(Math.min(b.endMin, win.endMin)) }))
    .filter((x) => x.bottom - x.top > 1);

  return (
    <div className="relative flex gap-1 md:gap-2" style={{ height }}>
      {/* Hour gutter — the true time axis. */}
      <div className="relative w-7 shrink-0 md:w-11">
        {ticks.map((t) => (
          <span
            key={t.min}
            className="absolute right-0 -translate-y-1/2 whitespace-nowrap text-[9px] tabular-nums text-[var(--muted)]/70 md:text-[11px]"
            style={{ top: t.y }}
          >
            {hhmm(t.min)}
          </span>
        ))}
      </div>

      {/* Place ribbon — where you were, strictly to scale. */}
      <div className="relative w-1.5 shrink-0 md:w-2">
        <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-[var(--border)]" />
        {bands.map(({ b, top, bottom }) => {
          const meta = placeMeta(b.category);
          // Deliberately NOT a link: the ribbon is 6px wide and sits right
          // beside the moment ticks, so tapping it is nearly always a mis-tap.
          // PlaceLegend below carries the same information at a real tap size.
          return (
            <span
              key={b.id}
              title={`${placeName(b)} · ${bandRange(b)} · ${fmtMins(b.dwellMins)}`}
              aria-label={`${placeName(b)}, ${bandRange(b)}`}
              className="absolute inset-x-0 rounded-full opacity-70"
              style={{ top, height: Math.max(3, bottom - top), backgroundColor: meta.colour }}
            />
          );
        })}
        {/* True-time tick for every moment, even when its row was displaced. */}
        {rows.map(({ m, tickY }) => (
          <span
            key={m.id}
            className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-[var(--surface)]"
            style={{
              top: tickY,
              width: 5 + Math.round(m.weight / 34),
              height: 5 + Math.round(m.weight / 34),
              backgroundColor: KIND_META[m.kind].colour,
              opacity: activeTopic && m.topicId !== activeTopic ? 0.25 : 1,
            }}
          />
        ))}
      </div>

      {/* Rail — leader lines back to true time, plus the topic curves. The only
          SVG on the page, at a fixed pixel width so strokes never distort. */}
      <svg
        width={RAIL_W}
        height={height}
        viewBox={`0 0 ${RAIL_W} ${height}`}
        // Pulled back over the column gap so each leader appears to leave its
        // own tick rather than starting a few pixels adrift of it.
        className="-ml-1 shrink-0 md:-ml-2"
        aria-hidden
        focusable="false"
      >
        {rows.map(({ m, tickY, cy }) => (
          <path
            key={`lead-${m.id}`}
            d={`M0,${tickY} C${RAIL_W * 0.6},${tickY} ${RAIL_W * 0.4},${cy} ${RAIL_W},${cy}`}
            fill="none"
            stroke="var(--border)"
            strokeWidth={1}
            opacity={activeTopic && m.topicId !== activeTopic ? 0.3 : 0.9}
          />
        ))}
        {data.topics.map((t) => {
          const members = t.momentIds
            .map((id) => rowById.get(id))
            .filter((r): r is MomentRow => Boolean(r))
            .sort((a, b) => a.cy - b.cy);
          if (members.length < 2) return null;
          const i = topicIndex.get(t.id) ?? 0;
          const colour = topicColour(i);
          const bulge = 3 + (i % 3) * 3;
          const dim = activeTopic !== null && activeTopic !== t.id;
          return (
            <g key={t.id} opacity={dim ? 0.15 : 0.55 + clamp(t.cohesion - 0.5, 0, 0.35)}>
              {members.slice(0, -1).map((a, k) => {
                const b = members[k + 1]!;
                return (
                  <path
                    key={`${t.id}-${k}`}
                    d={`M${RAIL_W},${a.cy} Q${bulge},${(a.cy + b.cy) / 2} ${RAIL_W},${b.cy}`}
                    fill="none"
                    stroke={colour}
                    strokeWidth={activeTopic === t.id ? 2 : 1.4}
                    strokeLinecap="round"
                  />
                );
              })}
            </g>
          );
        })}
      </svg>

      {/* Moments */}
      <div className="relative min-w-0 flex-1">
        {ticks.map((t) => (
          <span key={t.min} className="absolute inset-x-0 h-px bg-[var(--border)]/45" style={{ top: t.y }} />
        ))}
        {rows.map(({ m, y }) => {
          const meta = KIND_META[m.kind];
          const ti = m.topicId ? topicIndex.get(m.topicId) : undefined;
          const dim = activeTopic !== null && m.topicId !== activeTopic;
          const who = m.personIds.map((id) => peopleById.get(id)).filter((p): p is NonNullable<typeof p> => Boolean(p));
          const body = (
            <>
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[13px]"
                style={{ backgroundColor: `${meta.colour}22`, color: meta.colour }}
                aria-hidden
              >
                {momentIcon(m)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium leading-tight">{m.title}</span>
                  {(m.band === "urgent" || m.band === "important") && (
                    <span
                      className={`shrink-0 rounded-full px-1.5 py-px text-[9px] ${
                        m.band === "urgent" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"
                      }`}
                    >
                      {m.band}
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block truncate text-[10px] tabular-nums text-[var(--muted)]">{momentMeta(m)}</span>
                {who.length > 0 && (
                  <span className="mt-0.5 flex gap-1 overflow-hidden">
                    {who.slice(0, 4).map((p) => (
                      <span
                        key={p.id}
                        title={p.name}
                        className={`rounded-full px-1.5 py-px text-[9px] ${
                          p.isVip ? "bg-[var(--accent)]/20 text-[var(--accent)]" : "bg-white/5 text-[var(--muted)]"
                        }`}
                      >
                        {initials(p.name)}
                      </span>
                    ))}
                    {who.length > 4 && <span className="text-[9px] text-[var(--muted)]">+{who.length - 4}</span>}
                  </span>
                )}
              </span>
            </>
          );
          const cls =
            "absolute inset-x-0 flex items-start gap-2 rounded-lg border border-[var(--border)] bg-[var(--surface)] px-2 py-1.5 text-left transition-opacity hover:border-[var(--accent)]";
          const style: React.CSSProperties = {
            top: y,
            height: ROW_CONTENT_H,
            opacity: dim ? 0.35 : 1,
            borderLeftWidth: 3,
            borderLeftColor: ti === undefined ? "var(--border)" : topicColour(ti),
          };
          return m.href ? (
            <Link key={m.id} href={safeHref(m.href)} className={cls} style={style} onClick={() => onPickMoment?.(m)}>
              {body}
            </Link>
          ) : (
            <div key={m.id} className={cls} style={style}>
              {body}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function initials(name: string): string {
  // Unresolved people arrive as a raw handle ("priya.r@primoro.co.uk"), so take
  // the local part first — otherwise the domain supplies the second letter and
  // you get "PU" for Priya R.
  const s = (name || "?").trim();
  const base = s.includes("@") ? s.slice(0, s.indexOf("@")) : s;
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  if (parts.length > 1) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  return (parts[0] ?? s).slice(0, 2).toUpperCase();
}

// ---------------------------------------------------------------------------
// The compact horizontal overview (also used on /today)
// ---------------------------------------------------------------------------

export function DayStrip({ data, className = "" }: { data: DayMapResponse; className?: string }) {
  const { startMin, endMin } = data.window;
  const span = Math.max(60, endMin - startMin);
  const pct = (min: number) => `${clamp(((min - startMin) / span) * 100, 0, 100)}%`;
  const widthPct = (a: number, b: number) => `${clamp(((b - a) / span) * 100, 0, 100)}%`;

  const step = span > 720 ? 180 : 120;
  const ticks: number[] = [];
  for (let m = Math.ceil(startMin / step) * step; m <= endMin; m += step) ticks.push(m);

  return (
    <div className={className}>
      {/* Where you were */}
      <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-[var(--border)]/50">
        {data.places.map((b) => (
          <span
            key={b.id}
            title={`${placeName(b)} · ${bandRange(b)}`}
            className="absolute inset-y-0 rounded-full"
            style={{
              left: pct(Math.max(b.startMin, startMin)),
              width: widthPct(Math.max(b.startMin, startMin), Math.min(b.endMin, endMin)),
              backgroundColor: placeMeta(b.category).colour,
              opacity: 0.75,
            }}
          />
        ))}
      </div>

      {/* What happened */}
      <div className="relative mt-1.5 h-6 w-full">
        {ticks.map((m) => (
          <span key={m} className="absolute inset-y-0 w-px bg-[var(--border)]/60" style={{ left: pct(m) }} />
        ))}
        <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-[var(--border)]/60" />
        {data.moments.map((m) => {
          const size = 5 + Math.round(m.weight / 30);
          return (
            <span
              key={m.id}
              title={`${hhmm(m.startMin)} · ${m.title}`}
              className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-[var(--surface)]"
              style={{ left: pct(m.startMin), width: size, height: size, backgroundColor: KIND_META[m.kind].colour }}
            />
          );
        })}
      </div>

      <div className="mt-1 flex justify-between text-[9px] tabular-nums text-[var(--muted)]/70">
        <span>{hhmm(startMin)}</span>
        <span>{hhmm(endMin)}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small shared pieces
// ---------------------------------------------------------------------------

/** Place legend — the ribbon's key, and the only place labels are spelled out. */
export function PlaceLegend({ places }: { places: DayPlaceBand[] }) {
  if (places.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {places.map((b) => {
        const meta = placeMeta(b.category);
        return (
          <li key={b.id}>
            <Link
              href="/places"
              className="flex min-h-9 items-center gap-1.5 rounded-full border border-[var(--border)] px-2.5 py-2 text-[11px] hover:border-[var(--accent)]"
            >
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: meta.colour }} />
              <span className="max-w-[9rem] truncate">{placeName(b)}</span>
              <span className="tabular-nums text-[var(--muted)]">{bandRange(b)}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/** Topic chips — tap to highlight that thread of work through the day. */
export function TopicChips({
  topics,
  active,
  onToggle,
}: {
  topics: DayTopic[];
  active: string | null;
  onToggle: (id: string | null) => void;
}) {
  if (topics.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {topics.map((t, i) => {
        const on = active === t.id;
        const colour = topicColour(i);
        return (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => onToggle(on ? null : t.id)}
              aria-pressed={on}
              className="flex min-h-9 items-center gap-1.5 rounded-full border px-3 py-2 text-[11px] transition-colors"
              style={{
                borderColor: on ? colour : "var(--border)",
                backgroundColor: on ? `${colour}22` : "transparent",
                color: on ? colour : "var(--foreground)",
              }}
            >
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: colour }} />
              <span className="max-w-[11rem] truncate">{t.label}</span>
              <span className="tabular-nums text-[var(--muted)]">
                {fmtMins(t.minutes)} · {t.momentIds.length}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
